import { type ForwardedRef, forwardRef, type ReactNode } from "react";

import { type ButtonProps } from "antd/lib/button";
import { useTracking } from "react-tracking";
import { useMediaQuery } from "usehooks-ts";

import {
  BuiButton,
  type buiButtonTypes,
} from "@btnet/blueprint-components/BuiButton";
import { cn } from "@btnet/utilities-core";

import { Hotkey } from "../Hotkey/Hotkey";
import {
  type IHotkeyWithPopoverConfig,
  type THotkeyCommands,
} from "../Hotkey/hotkey.types";
import { getHotkeyProps } from "../Hotkey/hotkey.utility";
import { MD_BREAKPOINT } from "../types/breakpoints";
import "./BTButton.less";

export type BTButtonType = (typeof buiButtonTypes)[number];

export interface IBTButtonPropsBase
  extends Omit<ButtonProps, "type" | "icon" | "disabled" | "title" | "size"> {
  /**
   * @example
   * "save" | "delete" | "print" | "notify"
   */
  "data-testid": string;

  /**
   * Applies disabled styles to the button and attribute to button (Bubble up events will not be fired when disabled is true)
   * This is a change from BTButtonDeprecated, so please make sure to test any buttons that use the disabled prop to ensure it still meets your needs
   * The expected behavior for tooltips or popovers is that they will not show when the button is disabled. Instead the button should remain enabled and the onClick should open a confirmation modal or similar to tell the user why they can't perform the action, and the hotkey should also be disabled.
   *
   * @default false
   */
  disabled?: boolean;

  /**
   * @default "primary"
   */
  type?: BTButtonType;

  icon?: ReactNode;

  children?: ReactNode;

  /**
   * Reduce padding for use of buttons outside of button groups. Should only be used with tertirary type buttons in practice.
   * @default false
   * @deprecated This will no longer function and will be removed in a future release. Will be ignored if passed only around until conversion is complete.
   */
  isolated?: boolean;

  /**
   * Apply an underline to the button text. Should only be used with isolated tertiary buttons in practice.
   * @default false
   * @deprecated This will no longer function and will be removed in a future release. Will be ignored if passed only around until conversion is complete.
   */
  underline?: boolean;

  /**
   * Apply to remove box-shadow on focus/click
   * @deprecated This will no longer function and will be removed in a future release. Will be ignored if passed only around until conversion is complete.
   */
  noShadow?: boolean;

  /** When true, applies selected/active styling. Should only be used w/ tertiary buttons */
  selected?: boolean;

  hotkey?: THotkeyCommands | IHotkeyWithPopoverConfig;

  /**
   * When true, the button will collapse at the medium breakpoint (768px) if it has an icon.
   * @default false
   */
  collapse?: boolean;
}

interface IBTButtonPropsLoading<ActionType> extends IBTButtonPropsBase {
  /**
   * @example
   * <BTButton actionBeingPerformed={actionBeingPerformed} loadingAction="save" />
   */
  actionBeingPerformed: ActionType;

  /**
   * @example
   * <BTButton actionBeingPerformed={actionBeingPerformed} loadingAction="save" />
   */
  loadingAction: ActionType;
}

export type IBTButtonProps<ActionType> =
  | IBTButtonPropsBase
  | IBTButtonPropsLoading<ActionType>;

function BTButtonInner<ActionType>(
  props: IBTButtonProps<ActionType>,
  ref: ForwardedRef<HTMLElement>,
) {
  const { trackEvent, Track } = useTracking<Record<string, unknown>>({
    element: "Button",
    uniqueId: props["data-testid"],
  });
  const mdBreakpoint = useMediaQuery(`(max-width: ${MD_BREAKPOINT}px)`);
  const collapse = props.collapse && mdBreakpoint;

  const handleClick = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
    if (props.disabled) {
      event.preventDefault();
      return;
    }

    if (props.onClick) {
      props.onClick(event);
    }

    trackEvent({ event: "ButtonClick" });
  };

  const handleDoubleClick = (
    event: React.MouseEvent<HTMLElement, MouseEvent>,
  ) => {
    if (props.disabled) {
      event.preventDefault();
      return;
    }

    if (props.onDoubleClick) {
      props.onDoubleClick(event);
    }
  };

  const {
    children,
    icon,
    type,
    actionBeingPerformed,
    loadingAction,
    onClick: _onClick, // exclude onClick from the spread props.  its already being clobbered by handleClick
    className,
    tabIndex,
    hotkey,
    selected,
    ...otherProps
  } = props as IBTButtonPropsLoading<ActionType>;
  // note: actionBeingPerformed and loadingAction can be undefined

  // add loading props if they were passed
  let loadingProps: { loading?: boolean; disabled?: boolean } = {};
  if (
    "actionBeingPerformed" in props &&
    actionBeingPerformed !== undefined &&
    loadingAction !== undefined
  ) {
    loadingProps = {
      loading: actionBeingPerformed === loadingAction,
      disabled: actionBeingPerformed !== loadingAction,
    };
  }

  const loading = loadingProps.loading || !!props.loading;
  const disabled = loadingProps.disabled || props.disabled || loading;

  const shouldCollapse = collapse && icon;

  const button = (
    <Track>
      <BuiButton
        {...otherProps}
        ref={ref}
        type={type ?? "primary"}
        testid={props["data-testid"]}
        onClick={handleClick}
        onDoubleClick={handleDoubleClick}
        icon={icon}
        className={cn(className, "BTButton", { disabled })}
        tabIndex={disabled ? -1 : tabIndex}
        aria-disabled={disabled}
        disabled={disabled}
        loading={loading}
        selected={selected ?? false}
      >
        {!shouldCollapse && children}
      </BuiButton>
    </Track>
  );
  if (hotkey) {
    return (
      <Hotkey
        {...getHotkeyProps(hotkey)}
        disabled={disabled}
        onCommand={() => {
          if (!disabled) {
            handleClick({
              preventDefault: () => {},
            } as React.MouseEvent<HTMLElement, MouseEvent>);
          }
        }}
      >
        {button}
      </Hotkey>
    );
  } else {
    return button;
  }
}

/**
 * BTButton forwards its ref to the underlying button element. This is required
 * for patterns like BTDropdown's `triggerRef`, where focus must return to the
 * trigger when the menu closes.
 *
 * `forwardRef` erases the `<ActionType>` generic, so we cast the result back to a
 * generic-preserving call signature. This keeps `actionBeingPerformed` /
 * `loadingAction` type inference working for existing consumers.
 */
export const BTButton = forwardRef(BTButtonInner) as <ActionType>(
  props: IBTButtonProps<ActionType> & { ref?: ForwardedRef<HTMLElement> },
) => ReturnType<typeof BTButtonInner>;
